import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { AppLayout } from "@/components/layout/AppLayout";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { useLeads } from "@/hooks/useLeads";
import { useTasks } from "@/hooks/useTasks";
import { useChannelPartners } from "@/hooks/useChannelPartners";
import { useBankers } from "@/hooks/useBankers";
import { useAuth } from "@/contexts/AuthContext";
import { getLegacyCustomerName, LOAN_STATUS_CONFIG, LOAN_TYPE_LABELS, normalizeLoanType, CONNECTOR_TYPE_LABELS, Lead, TimelineItem, ChecklistItem, LoanStatus, Task } from "@/types";
import { addLeadStageChange } from "@/services/stageHistoryService";
import { 
  ArrowLeft, 
  Edit, 
  Share2, 
  Phone, 
  MessageCircle,
  FileText,
  Clock,
  MoveRight,
  CheckSquare,
  ClipboardList,
  Users,
  UserCircle,
  ExternalLink,
  Archive,
  ArchiveRestore,
  Send,
  Plus,
  Navigation,
  LogIn
} from "lucide-react";
import { AddNoteDialog } from "@/components/leads/AddNoteDialog";
import { AddReminderDialog } from "@/components/leads/AddReminderDialog";
import { FollowUpReminderDialog } from "@/components/leads/FollowUpReminderDialog";
import { MoveStageDialog } from "@/components/leads/MoveStageDialog";
import { ChecklistDialog } from "@/components/leads/ChecklistDialog";
import { ShareLeadDialog } from "@/components/leads/ShareLeadDialog";
import { EditLeadDialog } from "@/components/leads/EditLeadDialog";
import { DynamicQuestionnaireDialog } from "@/components/leads/DynamicQuestionnaireDialog";
import { DisbursementDialog } from "@/components/leads/DisbursementDialog";
import { LoginDialog } from "@/components/leads/LoginDialog";
import { AssociatedTasks } from "@/components/dashboard/AssociatedTasks";
import { 
  getMonthString, 
} from "@/services/incomeService";
import { calculateLeadIncomeServer } from "@/services/incomeCloudService";
import { formatDistanceToNow, format } from "date-fns";
import { toast } from "sonner";

function formatCurrency(amount: number) {
  if (amount >= 10000000) return `₹${(amount / 10000000).toFixed(1)}Cr`;
  if (amount >= 100000) return `₹${(amount / 100000).toFixed(0)}L`;
  return `₹${amount.toLocaleString("en-IN")}`;
}

const MAX_REMOVAL_REASON_LENGTH = 300;

export default function LeadDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { getLead, updateLead } = useLeads();
  const { tasks, addTask, updateTask, deleteTask } = useTasks();
  const { partners: channelPartners = [] } = useChannelPartners();
  const { bankers: smContacts = [] } = useBankers();
  const [lead, setLead] = useState(() => getLead(id || ''));
  
  // Update lead when id changes or leads are updated from external source
  useEffect(() => {
    const foundLead = getLead(id || '');
    if (foundLead) setLead(foundLead);
  }, [id, getLead]);

  const [noteOpen, setNoteOpen] = useState(false);
  const [reminderOpen, setReminderOpen] = useState(false);
  const [moveOpen, setMoveOpen] = useState(false);
  const [checklistOpen, setChecklistOpen] = useState(false);
  const [shareOpen, setShareOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [questionnaireOpen, setQuestionnaireOpen] = useState(false);
  const [disbursementOpen, setDisbursementOpen] = useState(false);
  const [loginOpen, setLoginOpen] = useState(false);
  const [timelineTab, setTimelineTab] = useState<'current' | 'archive'>('current');
  const [editingNoteId, setEditingNoteId] = useState<string | null>(null);
  const [editingNoteContent, setEditingNoteContent] = useState('');
  const [dailyUpdate, setDailyUpdate] = useState('');
  const [followUpOpen, setFollowUpOpen] = useState(false);
  const [pendingFollowUp, setPendingFollowUp] = useState(false);

  // Open follow-up dialog only after move dialog has fully closed to avoid
  // Radix UI pointer-event conflicts between the two simultaneous dialogs.
  useEffect(() => {
    if (!moveOpen && pendingFollowUp) {
      setFollowUpOpen(true);
      setPendingFollowUp(false);
    }
  }, [moveOpen, pendingFollowUp]);

  // Migrate existing timeline reminders to global tasks (one-time migration)
  useEffect(() => {
    if (!lead || !id) return;

    const timelineReminders = lead.timeline.filter(item => item.type === 'reminder');
    
    // Check if any timeline reminders exist that aren't already in global tasks
    timelineReminders.forEach(async (reminder) => {
      // Check if this reminder already exists in global tasks by checking if task with same leadId and similar content exists
      const existingTask = tasks.find(t => 
        t.leadId === id && 
        t.description === reminder.content &&
        t.type === 'reminder'
      );
      
      if (!existingTask) {
        // Migrate this reminder to global tasks
        try {
          await addTask({
            title: reminder.content.split('\n')[0].substring(0, 50),
            description: reminder.content,
            type: 'reminder',
            leadId: id,
            dueDate: reminder.dueDate || new Date(),
            priority: 'medium',
            completed: reminder.completed || false,
            createdBy: reminder.createdBy,
            createdAt: reminder.createdAt,
          });
          
          // Remove the migrated reminder from timeline
          const updated = {
            ...lead,
            timeline: lead.timeline.filter(item => item.id !== reminder.id)
          };
          setLead(updated);
          updateLead(id, updated);
        } catch (error) {
          console.error('Error migrating reminder to global tasks:', error);
        }
      }
    });
  }, [lead?.id, id]); // Only run when lead ID changes

  if (!lead) {
    return (
      <AppLayout>
        <div className="p-8 text-center">
          <p>Lead not found</p>
          <Button onClick={() => navigate("/leads")} className="mt-4">
            Back to Leads
          </Button>
        </div>
      </AppLayout>
    );
  }

  const statusConfig = LOAN_STATUS_CONFIG[lead.status];
  const customerName = getLegacyCustomerName(lead);
  
  // Get linked channel partner and Banker contact from real Firestore data
  const channelPartner = lead.connectorId ? channelPartners.find(c => c.id === lead.connectorId) : null;
  const smContact = lead.smContactId ? smContacts.find(sm => sm.id === lead.smContactId) : null;

  const handleSaveLead = (updatedData: Partial<Lead>) => {
    const updated = { ...lead, ...updatedData };
    setLead(updated);
    if (id) updateLead(id, updated);
  };

  const handleAddNote = (content: string) => {
    const newNote: TimelineItem = {
      id: `note_${Date.now()}`,
      type: 'note',
      content,
      createdAt: new Date(),
      createdBy: 'You',
    };
    const updated = {
      ...lead,
      timeline: [newNote, ...(lead.timeline || [])]
    };
    setLead(updated);
    if (id) updateLead(id, updated);
  };

  const handleAddReminder = (content: string, dueDate: Date) => {
    // Reminder is already added to global tasks by AddReminderDialog
    // No need to add to timeline anymore
    toast.success("Reminder added successfully");
  };

  const handleAddDailyUpdate = () => {
    if (!dailyUpdate.trim()) {
      toast.error("Please enter an update");
      return;
    }
    
    const updateText = dailyUpdate.trim();
    const datePrefix = format(new Date(), 'dd MMM yyyy');

    // 1. Save as a daily-update timeline item (tracked separately)
    const newUpdate: TimelineItem = {
      id: `update_${Date.now()}`,
      type: 'note',
      content: updateText,
      createdAt: new Date(),
      createdBy: 'You',
      isDailyUpdate: true,
    };

    // 2. Also save a plain note with date prefix so it surface in the Notes section
    const newNote: TimelineItem = {
      id: `note_${Date.now() + 1}`,
      type: 'note',
      content: `${datePrefix}: ${updateText}`,
      createdAt: new Date(),
      createdBy: 'You',
    };

    const updated = {
      ...lead,
      timeline: [newNote, newUpdate, ...(lead.timeline || [])]
    };
    setLead(updated);
    if (id) updateLead(id, updated);
    
    setDailyUpdate('');
    toast.success("Daily update added");
  };

  const handleArchiveItem = async (itemId: string) => {
    // Check if this is a global task or a timeline note
    const task = tasks.find(t => t.id === itemId);
    
    if (task) {
      // This is a global task - mark as completed
      await updateTask(itemId, { completed: true });
      toast.success("Reminder completed and moved to completed items");
    } else {
      // This is a timeline note - update timeline
      const updated = {
        ...lead,
        timeline: lead.timeline.map(item => 
          item.id === itemId 
            ? { ...item, archived: true } 
            : item
        )
      };
      setLead(updated);
      if (id) updateLead(id, updated);
      toast.success("Moved to completed");
    }
  };

  const handleUnarchiveItem = async (itemId: string) => {
    // Check if this is a global task or a timeline note
    const task = tasks.find(t => t.id === itemId);
    
    if (task) {
      // This is a global task - mark as not completed
      await updateTask(itemId, { completed: false });
      toast.success("Reminder restored from completed items");
    } else {
      // This is a timeline note - update timeline
      const updated = {
        ...lead,
        timeline: lead.timeline.map(item => 
          item.id === itemId 
            ? { ...item, archived: false } 
            : item
        )
      };
      setLead(updated);
      if (id) updateLead(id, updated);
      toast.success("Restored from completed");
    }
  };

  const handleSaveNoteEdit = (noteId: string) => {
    if (!editingNoteContent.trim()) {
      toast.error("Note cannot be empty");
      return;
    }
    const updated = {
      ...lead,
      timeline: lead.timeline.map(item =>
        item.id === noteId ? { ...item, content: editingNoteContent.trim() } : item
      )
    };
    setLead(updated);
    if (id) updateLead(id, updated);
    setEditingNoteId(null);
    setEditingNoteContent('');
    toast.success("Note updated");
  };

  const handleSaveQuestionnaire = (data: any) => {
    const updated = { ...lead, questionnaireData: data };
    setLead(updated);
    if (id) updateLead(id, updated);
  };

  const handleSaveChecklist = (checklist: ChecklistItem[]) => {
    const updated = { ...lead, checklist };
    setLead(updated);
    if (id) updateLead(id, updated);
  };

  const handleSaveDisbursement = async (updates: Partial<Lead>) => {
    const updated = { ...lead, ...updates };
    setLead(updated);
    if (id) {
      await updateLead(id, updates);
      
      // Trigger income calculation if lead is fully disbursed
      if (updates.status === 'disbursed' && updated.disbursedAmount && id) {
        try {
          // Call server-side income calculation
          await calculateLeadIncomeServer(id);
          console.log('Income calculated for disbursed lead:', id);
        } catch (error) {
          console.error('Failed to calculate income for lead:', error);
          // Don't show error to user - income calculation failure shouldn't block disbursement
        }
      }
    }
  };

  const handleSaveLogin = async (updates: Partial<Lead>) => {
    const updated = { ...lead, ...updates };
    setLead(updated);
    if (id) {
      await updateLead(id, updates);
    }
  };

const handleMoveStage = (newStatus: LoanStatus, removalReason = "") => {
  const normalizedRemovalReason = removalReason
    .trim()
    .replace(/\s+/g, " ")
    .slice(0, MAX_REMOVAL_REASON_LENGTH);

    let removalNote: TimelineItem | null = null;
    if(newStatus === 'removed' && normalizedRemovalReason){
      removalNote = {
        id: `note_${Date.now()}`,
        type: "note",
        content: `Lead removed. Reason: ${normalizedRemovalReason}`,
        createdAt: new Date(),
        createdBy: "You",
      };
    }
    const updated = { 
      ...lead, 
      status: newStatus,
      timeline: removalNote ? [removalNote, ...(lead.timeline || [])] : lead.timeline
     };
    setLead(updated);
    if (id) {
      updateLead(id, updated);
      // Track stage change for leaderboard (especially logged_in)
      addLeadStageChange({
        leadId: id,
        fromStatus: lead.status,
        toStatus: newStatus,
        changedBy: user?.userId || user?.id || 'Unknown',
        changedAt: new Date(),
        officeId: user?.officeId || '',
      });
    }
  };

  const handleFollowUpReminderSave = (_content: string, _dueDate: Date) => {
    handleMoveStage('followup_later', '');
    toast.success("Stage moved to Followup Later and reminder added");
  };

  // Filter timeline items and combine with global tasks for this lead
  // Timeline only contains notes now (reminders are in global tasks)
  const timelineNotes = lead.timeline.filter(item => 
    item.type === 'note' && !item.archived && !item.isDailyUpdate
  );
  const archivedTimelineNotes = lead.timeline.filter(item => 
    item.type === 'note' && item.archived
  );
  
  // Get reminders from global tasks for this lead
  const leadTasks = tasks.filter(t => t.leadId === id && t.type === 'reminder');
  const activeTasks = leadTasks.filter(t => !t.completed);
  const completedTasks = leadTasks.filter(t => t.completed);
  
  // Combine timeline notes and active tasks for "Current" tab
  const currentItems = [
    ...timelineNotes,
    ...activeTasks.map(task => ({
      id: task.id,
      type: 'reminder' as const,
      content: task.description,
      createdAt: task.createdAt,
      createdBy: task.createdBy,
      dueDate: task.dueDate,
      completed: task.completed,
      isGlobalTask: true, // Flag to identify this came from global tasks
    }))
  ].sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
  
  // Combine archived timeline notes and completed tasks for "Archive" tab
  const archivedItems = [
    ...archivedTimelineNotes,
    ...completedTasks.map(task => ({
      id: task.id,
      type: 'reminder' as const,
      content: task.description,
      createdAt: task.createdAt,
      createdBy: task.createdBy,
      dueDate: task.dueDate,
      completed: task.completed,
      archived: true,
      isGlobalTask: true, // Flag to identify this came from global tasks
    }))
  ].sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
  
  // Get today's daily updates (not archived, created today)
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const todaysDailyUpdates = lead.timeline.filter(item => {
    const itemDate = new Date(item.createdAt);
    itemDate.setHours(0, 0, 0, 0);
    return item.isDailyUpdate && !item.archived && itemDate.getTime() === today.getTime();
  });
  
  // Check if daily updates should be disabled (disbursed or later stages)
  const isDailyUpdateDisabled = ['partial_disbursed', 'disbursed', 'otc', 'payout_released', 'closed', 'removed', 'followup_later'].includes(lead.status);

  return (
    <AppLayout>
      <div
        className="flex flex-col md:h-screen md:h-dvh"
        style={{ height: "calc(var(--viewport-height) - 80px)" }}
      >
        {/* Header - Sticky */}
        <div className="sticky top-0 z-10 bg-background border-b">
          <div className="flex items-center justify-between p-4">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => navigate("/leads")}
            >
              <ArrowLeft className="h-5 w-5" />
            </Button>
            <h1 className="font-semibold">Customer Details</h1>
            <div className="flex gap-2">
              <Button
                variant="success"
                size="icon"
                onClick={() => setShareOpen(true)}
                className="h-9 w-9 rounded-full"
              >
                <Share2 className="h-5 w-5" />
              </Button>
            </div>
          </div>
        </div>

        {/* Content - Scrollable */}
        <div className="flex-1 overflow-y-auto">
          <div className="p-4 space-y-4 max-w-2xl mx-auto pb-20 md:pb-24">
            {/* Customer Info - More Compact */}
            <Card variant="elevated">
              <CardContent className="p-4 space-y-3">
                <div className="flex items-start justify-between">
                  <div>
                    <h2 className="text-xl font-bold">{customerName}</h2>
                    {lead.panCard && (
                      <p className="text-xs text-muted-foreground mt-0.5">
                        PAN: {lead.panCard}
                      </p>
                    )}
                  </div>
                  <div className="flex items-center gap-1.5">
                    <Badge
                      variant={statusConfig.badgeVariant as any}
                      className="text-xs"
                    >
                      {statusConfig.label}
                    </Badge>
                    <Button
                      size="sm"
                      variant="secondary"
                      onClick={() => setEditOpen(true)}
                      className="h-8 w-8 p-0 rounded-full"
                    >
                      <Edit className="h-4 w-4" />
                    </Button>
                  </div>
                </div>

                {/* Contact Actions */}
                <div className="flex gap-2">
                  <Button
                    variant="secondary"
                    size="sm"
                    className="flex-1 h-9"
                    onClick={() => window.open(`tel:${lead.isNRI ? "+" : ""}${lead.mobile}`)}
                  >
                    <Phone className="h-3.5 w-3.5 mr-1.5" />
                    Call
                  </Button>
                  <Button
                    variant="success"
                    size="sm"
                    className="flex-1 h-9"
                    onClick={() => {
                      const phone = lead.mobile?.replace(/\D/g, "");
                      // NRI numbers already store the destination country's calling
                      // code prefixed (see CreateLeadSheet/EditLeadDialog); only
                      // domestic numbers need "91" prepended for wa.me.
                      const whatsappNumber = lead.isNRI
                        ? phone
                        : (phone?.startsWith("91") ? phone : `91${phone}`);
                      window.open(`https://wa.me/${whatsappNumber}`);
                    }}
                  >
                    <MessageCircle className="h-3.5 w-3.5 mr-1.5" />
                    WhatsApp
                  </Button>
                  {lead.location && (
                    <Button
                      variant="outline"
                      size="sm"
                      className="h-9 px-2.5"
                      onClick={() =>
                        window.open(
                          `https://www.google.com/maps/dir/?api=1&destination=${lead.location!.lat},${lead.location!.lng}`,
                          "_blank",
                        )
                      }
                      title="Navigate"
                    >
                      <Navigation className="h-3.5 w-3.5" />
                    </Button>
                  )}
                </div>

                {/* Details Grid - More Compact */}
                <div className="grid grid-cols-2 gap-3 pt-2 border-t">
                  {lead.loanAmount && (
                    <div>
                      <p className="text-[10px] text-muted-foreground">
                        Loan Amount
                      </p>
                      <p className="font-semibold text-sm">
                        {formatCurrency(lead.loanAmount)}
                      </p>
                    </div>
                  )}
                  {lead.loanTenure && (
                    <div>
                      <p className="text-[10px] text-muted-foreground">
                        Tenure
                      </p>
                      <p className="font-semibold text-sm">
                        {lead.loanTenure} years
                      </p>
                    </div>
                  )}
                  {lead.lender && (
                    <div>
                      <p className="text-[10px] text-muted-foreground">
                        Lender
                      </p>
                      <p className="font-semibold text-sm">{lead.lender}</p>
                    </div>
                  )}
                  {lead.loanType && (
                    <div>
                      <p className="text-[10px] text-muted-foreground">Type</p>
                      <p className="font-semibold text-sm">
                        {LOAN_TYPE_LABELS[normalizeLoanType(lead.loanType) as keyof typeof LOAN_TYPE_LABELS] || lead.loanType}
                      </p>
                    </div>
                  )}
                </div>

                {/* BT / Top-Up Info */}
                {(lead.isBT || lead.isTopUp) && (
                  <div className="grid grid-cols-2 gap-3 pt-2 border-t">
                    {lead.isBT && (
                      <>
                        <div>
                          <p className="text-[10px] text-muted-foreground">
                            Balance Transfer
                          </p>
                          <Badge variant="info" className="text-xs mt-0.5">
                            BT Case
                          </Badge>
                        </div>
                        {lead.existingLender && (
                          <div>
                            <p className="text-[10px] text-muted-foreground">
                              Existing Lender
                            </p>
                            <p className="font-semibold text-sm">
                              {lead.existingLender}
                            </p>
                          </div>
                        )}
                        {lead.outstandingAmount && (
                          <div>
                            <p className="text-[10px] text-muted-foreground">
                              Outstanding Amount
                            </p>
                            <p className="font-semibold text-sm">
                              {formatCurrency(lead.outstandingAmount)}
                            </p>
                          </div>
                        )}
                      </>
                    )}
                    {lead.isTopUp && (
                      <>
                        <div>
                          <p className="text-[10px] text-muted-foreground">
                            Top-Up
                          </p>
                          <Badge variant="accent" className="text-xs mt-0.5">
                            Top-Up Case
                          </Badge>
                        </div>
                        {lead.topUpAmount && (
                          <div>
                            <p className="text-[10px] text-muted-foreground">
                              Top-Up Amount
                            </p>
                            <p className="font-semibold text-sm">
                              {formatCurrency(lead.topUpAmount)}
                            </p>
                          </div>
                        )}
                      </>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Daily Update Section */}
            {!isDailyUpdateDisabled ? (
              <Card variant="elevated">
                <CardContent className="p-4">
                  <div className="flex items-center gap-2 mb-3">
                    <FileText className="h-4 w-4 text-[#4ea39f]" />
                    <h3 className="font-semibold text-sm">Today's Update</h3>
                  </div>

                  {/* Show today's updates or input field */}
                  {todaysDailyUpdates.length > 0 ? (
                    <div className="space-y-2">
                      {todaysDailyUpdates.map((update) => (
                        <p key={update.id} className="text-sm text-foreground">
                          {update.content}
                        </p>
                      ))}
                    </div>
                  ) : (
                    <div className="flex gap-2">
                      <Input
                        placeholder="Add daily update for this customer..."
                        value={dailyUpdate}
                        onChange={(e) => setDailyUpdate(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === "Enter" && !e.shiftKey) {
                            e.preventDefault();
                            handleAddDailyUpdate();
                          }
                        }}
                        className="flex-1 h-9 text-sm"
                      />
                      <Button
                        size="sm"
                        className="h-9 px-3 bg-gradient-to-r from-[#2d5a6b] to-[#4ea39f] hover:from-[#234851] hover:to-[#3d8985]"
                        onClick={handleAddDailyUpdate}
                        disabled={!dailyUpdate.trim()}
                      >
                        <Send className="h-4 w-4" />
                      </Button>
                    </div>
                  )}
                </CardContent>
              </Card>
            ) : (
              <Card variant="elevated" className="border-dashed">
                <CardContent className="p-4">
                  <div className="flex items-center gap-3 text-muted-foreground">
                    <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-muted">
                      <FileText className="h-4 w-4" />
                    </div>
                    <div>
                      <p className="text-sm font-medium">
                        Daily updates disabled
                      </p>
                      <p className="text-xs">
                        Updates are not available for disbursed cases
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Channel Partner Info */}
            {channelPartner && (
              <Card variant="elevated">
                <CardContent className="p-4">
                  <div className="flex items-start justify-between">
                    <div className="flex items-center gap-3">
                      <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-accent/10 text-accent font-bold">
                        <Users className="h-5 w-5" />
                      </div>
                      <div>
                        <p className="text-xs text-muted-foreground">
                          Channel Partner
                        </p>
                        <p className="font-semibold">{channelPartner.name}</p>
                        <Badge variant="secondary" className="text-[10px] mt-1">
                          {CONNECTOR_TYPE_LABELS[channelPartner.type]}
                        </Badge>
                      </div>
                    </div>
                    <div className="flex gap-1">
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8"
                        onClick={() =>
                          window.open(`tel:${channelPartner.whatsappNumber}`)
                        }
                      >
                        <Phone className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8"
                        onClick={() => {
                          const n = channelPartner.whatsappNumber.replace(
                            /\D/g,
                            "",
                          );
                          window.open(
                            `https://wa.me/${n.startsWith("91") && n.length > 10 ? n : "91" + n}`,
                          );
                        }}
                      >
                        <MessageCircle className="h-4 w-4 text-success" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8"
                        onClick={() =>
                          navigate(
                            `/tools/channel-partners/${channelPartner.id}`,
                          )
                        }
                      >
                        <ExternalLink className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Banker Contact Info */}
            {smContact && (
              <Card variant="elevated">
                <CardContent className="p-4">
                  <div className="flex items-start justify-between">
                    <div className="flex items-center gap-3">
                      <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-warning/10 text-warning font-bold">
                        <UserCircle className="h-5 w-5" />
                      </div>
                      <div>
                        <p className="text-xs text-muted-foreground">
                          Banker Contact
                        </p>
                        <p className="font-semibold">{smContact.name}</p>
                        <p className="text-xs text-muted-foreground">
                          {smContact.designation || "Banker"} • {smContact.bank}
                        </p>
                      </div>
                    </div>
                    <div className="flex gap-1">
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8"
                        onClick={() => window.open(`tel:${smContact.phone}`)}
                      >
                        <Phone className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8"
                        onClick={() => {
                          const n = smContact.phone.replace(/\D/g, "");
                          window.open(
                            `https://wa.me/${n.startsWith("91") && n.length > 10 ? n : "91" + n}`,
                          );
                        }}
                      >
                        <MessageCircle className="h-4 w-4 text-success" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8"
                        onClick={() =>
                          navigate(`/tools/sm-contact/${smContact.id}`)
                        }
                      >
                        <ExternalLink className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Quick Actions - Desktop only */}
            <div className="hidden md:grid md:grid-cols-2 gap-3">
              <Button
                variant="outline"
                className="justify-start"
                onClick={() => setQuestionnaireOpen(true)}
              >
                <ClipboardList className="h-4 w-4 mr-2" />
                Customer Questionnaire
              </Button>
              <Button
                variant="outline"
                className="justify-start"
                onClick={() => setDisbursementOpen(true)}
              >
                <span className="mr-2 font-bold text-base">₹</span>
                Disbursement Details
              </Button>
            </div>

            {/* Associated Reminders */}
            <AssociatedTasks
              entityType="lead"
              entityId={lead.id}
              onAddReminder={() => setReminderOpen(true)}
            />

            {/* Timeline with Tabs - Notes Only */}
            <div className="pb-6">
              <h3 className="font-semibold mb-3 px-1">Notes</h3>
              <Tabs
                value={timelineTab}
                onValueChange={(v) =>
                  setTimelineTab(v as "current" | "archive")
                }
              >
                <TabsList className="grid w-full grid-cols-2 mb-3">
                  <TabsTrigger value="current" className="text-sm">
                    Active ({timelineNotes.length})
                  </TabsTrigger>
                  <TabsTrigger value="archive" className="text-sm">
                    Completed ({archivedTimelineNotes.length})
                  </TabsTrigger>
                </TabsList>

                <TabsContent value="current" className="mt-0 space-y-2">
                  {timelineNotes.length > 0 ? (
                    timelineNotes.map((item) => (
                      <Card key={item.id}>
                        <CardContent className="p-4">
                          <div className="flex items-start gap-3">
                            <div className="mt-0.5 rounded-full p-2 bg-info/10">
                              <FileText className="h-4 w-4 text-info" />
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className="text-sm whitespace-pre-wrap break-words">
                                {item.content}
                              </p>
                              <p className="text-xs text-muted-foreground mt-1">
                                {formatDistanceToNow(item.createdAt, {
                                  addSuffix: true,
                                })}{" "}
                                by {item.createdBy}
                              </p>
                            </div>
                            <div className="flex items-center gap-0.5 flex-shrink-0">
                              <Button
                                variant="ghost"
                                size="icon"
                                className="h-8 w-8"
                                onClick={() => {
                                  setEditingNoteId(item.id);
                                  setEditingNoteContent(item.content);
                                }}
                                title="Edit note"
                              >
                                <Edit className="h-3.5 w-3.5" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="icon"
                                className="h-8 w-8"
                                onClick={() => handleArchiveItem(item.id)}
                                title="Mark completed"
                              >
                                <Archive className="h-3.5 w-3.5" />
                              </Button>
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    ))
                  ) : (
                    <Card>
                      <CardContent className="p-8 text-center text-muted-foreground">
                        <FileText className="h-8 w-8 mx-auto mb-2 opacity-50" />
                        <p className="text-sm">No active notes</p>
                      </CardContent>
                    </Card>
                  )}
                </TabsContent>

                <TabsContent value="archive" className="mt-0 space-y-2">
                  {archivedTimelineNotes.length > 0 ? (
                    archivedTimelineNotes.map((item) => (
                      <Card key={item.id} className="opacity-60">
                        <CardContent className="p-4">
                          <div className="flex items-start gap-3">
                            <div className="mt-0.5 rounded-full p-2 bg-info/10">
                              <FileText className="h-4 w-4 text-info" />
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className="text-sm whitespace-pre-wrap break-words">{item.content}</p>
                              <p className="text-xs text-muted-foreground mt-1">
                                {formatDistanceToNow(item.createdAt, {
                                  addSuffix: true,
                                })}{" "}
                                by {item.createdBy}
                              </p>
                            </div>
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-8 w-8 flex-shrink-0"
                              onClick={() => handleUnarchiveItem(item.id)}
                              title="Restore note"
                            >
                              <ArchiveRestore className="h-4 w-4" />
                            </Button>
                          </div>
                        </CardContent>
                      </Card>
                    ))
                  ) : (
                    <Card>
                      <CardContent className="p-8 text-center text-muted-foreground">
                        <Archive className="h-8 w-8 mx-auto mb-2 opacity-50" />
                        <p className="text-sm">No completed notes</p>
                      </CardContent>
                    </Card>
                  )}
                </TabsContent>
              </Tabs>
            </div>
          </div>
        </div>

        {/* Action Dock - Fixed Bottom on mobile, Sticky on desktop */}
        <div
          className="fixed md:sticky left-0 right-0 border-t bg-background z-40 md:bottom-0"
          style={{
            bottom:
              "calc(var(--footer-height, 56px) + env(safe-area-inset-bottom, 0px))",
          }}
        >
          <div className="grid grid-cols-6 gap-1 md:gap-2 p-2 md:p-3 max-w-2xl mx-auto">
            <Button
              variant="outline"
              className="flex flex-col h-auto py-2 md:py-3 gap-1"
              onClick={() => setNoteOpen(true)}
            >
              <FileText className="h-4 w-4 md:h-5 md:w-5" />
              <span className="text-[10px] md:text-xs">Note</span>
            </Button>
            <Button
              variant="outline"
              className="flex flex-col h-auto py-2 md:py-3 gap-1"
              onClick={() => setLoginOpen(true)}
            >
              <LogIn className="h-4 w-4 md:h-5 md:w-5" />
              <span className="text-[10px] md:text-xs">Login</span>
            </Button>
            <Button
              variant="outline"
              className="flex flex-col h-auto py-2 md:py-3 gap-1"
              onClick={() => setMoveOpen(true)}
            >
              <MoveRight className="h-4 w-4 md:h-5 md:w-5" />
              <span className="text-[10px] md:text-xs">Move</span>
            </Button>
            <Button
              variant="outline"
              className="flex flex-col h-auto py-2 md:py-3 gap-1"
              onClick={() => setQuestionnaireOpen(true)}
            >
              <ClipboardList className="h-4 w-4 md:h-5 md:w-5" />
              <span className="text-[10px] md:text-xs">Ques</span>
            </Button>
            <Button
              variant="outline"
              className="flex flex-col h-auto py-2 md:py-3 gap-1"
              onClick={() => setChecklistOpen(true)}
            >
              <CheckSquare className="h-4 w-4 md:h-5 md:w-5" />
              <span className="text-[10px] md:text-xs">Check</span>
            </Button>
            <Button
              variant="outline"
              className="flex flex-col h-auto py-2 md:py-3 gap-1"
              onClick={() => setDisbursementOpen(true)}
            >
              <span className="font-semibold text-sm md:text-base leading-none">
                ₹
              </span>
              <span className="text-[10px] md:text-xs">Disburse</span>
            </Button>
          </div>
        </div>
      </div>

      {/* Dialogs */}
      <EditLeadDialog
        open={editOpen}
        onOpenChange={setEditOpen}
        lead={lead}
        onSave={handleSaveLead}
      />
      <AddNoteDialog
        open={noteOpen}
        onOpenChange={setNoteOpen}
        leadId={lead.id}
        onSave={handleAddNote}
      />
      <AddReminderDialog
        open={reminderOpen}
        onOpenChange={setReminderOpen}
        leadId={lead.id}
        customerName={getLegacyCustomerName(lead)}
        connectorId={lead.connectorId}
        smContactId={lead.smContactId}
        onSave={handleAddReminder}
      />
      <FollowUpReminderDialog
        open={followUpOpen}
        onOpenChange={setFollowUpOpen}
        leadId={lead.id}
        customerName={getLegacyCustomerName(lead)}
        connectorId={lead.connectorId}
        smContactId={lead.smContactId}
        onSave={handleFollowUpReminderSave}
      />
      <MoveStageDialog
        open={moveOpen}
        onOpenChange={setMoveOpen}
        lead={lead}
        onSave={handleMoveStage}
        onFollowUpRequired={() => setPendingFollowUp(true)}
      />
      <ChecklistDialog
        open={checklistOpen}
        onOpenChange={setChecklistOpen}
        lead={lead}
        onSave={handleSaveChecklist}
      />
      <ShareLeadDialog
        open={shareOpen}
        onOpenChange={setShareOpen}
        lead={lead}
      />
      <DynamicQuestionnaireDialog
        open={questionnaireOpen}
        onOpenChange={setQuestionnaireOpen}
        lead={lead}
        onSave={handleSaveQuestionnaire}
      />
      <DisbursementDialog
        open={disbursementOpen}
        onOpenChange={setDisbursementOpen}
        lead={lead}
        onSave={handleSaveDisbursement}
      />
      <LoginDialog
        open={loginOpen}
        onOpenChange={setLoginOpen}
        lead={lead}
        onSave={handleSaveLogin}
      />

      {/* Edit Note Dialog */}
      <Dialog
        open={editingNoteId !== null}
        onOpenChange={(open) => {
          if (!open) {
            setEditingNoteId(null);
            setEditingNoteContent("");
          }
        }}
      >
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Edit className="h-4 w-4" />
              Edit Note
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 pt-2">
            <Textarea
              value={editingNoteContent}
              onChange={(e) => setEditingNoteContent(e.target.value)}
              placeholder="Enter note content..."
              rows={4}
              className="resize-none"
              autoFocus
            />
            <div className="flex gap-3">
              <Button
                variant="outline"
                className="flex-1 h-11"
                onClick={() => {
                  setEditingNoteId(null);
                  setEditingNoteContent("");
                }}
              >
                Cancel
              </Button>
              <Button
                className="flex-1 h-11"
                onClick={() => {
                  if (editingNoteId) handleSaveNoteEdit(editingNoteId);
                }}
              >
                Save Note
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </AppLayout>
  );
}
